export const GIFT_FINDER_LABEL = 'today/label/gift-finder-2019';

export const DEFAULT_PRICE = '[0 TO *]';
export const WAFFLE_REQUEST_SIZE = 30;

const FILTER_RECIPIENT = 'recipientFilter';
const FILTER_INTERESTS = 'interestsFilter';
const FILTER_PRICE = 'priceFilter';

export const filterKeys = [
  FILTER_RECIPIENT,
  FILTER_INTERESTS,
  FILTER_PRICE,
];

// Sort products with 'best sellers' label first
export const giftFinderTaxonomyOverride = `taxonomy:(${GIFT_FINDER_LABEL} AND today/label/shop-best-sellers) OR taxonomy:(${GIFT_FINDER_LABEL})`;
export const giftFinderSortOverride = 'taxonomy:desc,datePublished:desc';

export function alterFilterActiveProperty(filter, active = true) {
  return filter.map((option) => Object.assign(option, { active }));
}

// Extract the min and max values from a graphql range string
export function extractPriceValues(price) {
  const exec = /([\d.*]+)\sTO\s([\d.*]+)/.exec(price);
  let [, min, max] = exec;
  // min val
  min = typeof min === 'string' ? parseFloat(min) : 0;
  // max val
  max = max && max === '*' ? '*' : parseFloat(max);
  // return array
  return [min, max];
}

// Format two numbers into a range string usable in a graphl search query
export function formatPriceRange(min, max) {
  return `[${min || 0} TO ${max && !Number.isNaN(max) ? max : '*'}]`;
}

// Find the low and high values from a set of price filters
export function aggregatePriceRangeFromFilters(filters) {
  const min = [];
  const max = [];

  filters.forEach((value, key) => {
    if (value.category === FILTER_PRICE) {
      const [minVal, maxVal] = extractPriceValues(key);
      min.push(minVal);
      max.push(maxVal);
    }
  });

  if (min.length) {
    return formatPriceRange(
      Math.min(...min),
      Math.max(...max),
    );
  }

  return false;
}

// Make a deeper copy of an array of objects
const copy = (array) => array.map((arr) => ({ ...arr }));

// Set the active property for the object at the given index
// as well as the next item if it also exists in the array
export function enableSelfAndNext(categories, index) {
  // Make copy
  const modified = copy(categories);
  // Update self
  modified[index].active = true;
  // Check if there is an item at the next index
  if (index + 1 < modified.length) {
    modified[index + 1].active = true;
  }
  return modified;
}

// Set the active property for the object at the given index
// as well as the previous item if it also exists in the array
export function enableSelfAndPrevious(categories, index) {
  // Make copy
  const modified = copy(categories);
  // Update self
  modified[index].active = true;
  // Check if there is an item at the previous index
  if (index - 1 >= 0) {
    modified[index - 1].active = true;
  }
  return modified;
}

export function updateEnabledPriceFilters(categories, filters) {
  const enabled = Object.keys(filters)
    // Get numeric positions of enabled filters
    .reduce((accumulator, currentVal, currentIndex) => {
      if (filters[currentVal]) {
        return [...accumulator, currentIndex];
      }
      return accumulator;
    }, []);

  if (enabled.length) {
    // Disable ALL inputs
    let modified = alterFilterActiveProperty(categories, false);
    // Selectively toggle disabled state
    if (enabled.length === 1) {
      // Single, enable self and adjacent
      modified = enableSelfAndPrevious(modified, enabled[0]);
      modified = enableSelfAndNext(modified, enabled[0]);
    } else {
      // Multiple, modify first and last
      modified = enableSelfAndPrevious(modified, enabled[0]);
      modified = enableSelfAndNext(modified, enabled[enabled.length - 1]);
    }
    // Return
    return modified;
  }

  // None enabled, re-enable ALL price filter inputs
  return alterFilterActiveProperty(categories, true);
}


const constructQueryObj = (filters = {}, giftFinderType) => {
  if (Object.keys(filters).length === 0) {
    return giftFinderType;
  }
  // Always filtered by label taxonomy
  const query = [`taxonomy:(${giftFinderType})`];
  // Combine selected recipient and interest taxonomy filters
  [FILTER_RECIPIENT, FILTER_INTERESTS]
    .forEach((key) => {
      const eachFilterQuery = [];
      // Find enabled filters
      Object.keys(filters[key])
        .forEach((taxonomy) => {
          if (filters[key][taxonomy]) {
            eachFilterQuery.push(taxonomy);
          }
        });
      if (eachFilterQuery.length) {
        query.push(`taxonomy:(${eachFilterQuery.join(' OR ')})`);
      }
    });
  return query.join(' AND ');
};

export const extractFromMetadata = (metadata = null) => {
  if (!metadata) {
    return {};
  }
  const categories = {};
  const filterValues = {};
  const displayNameByKey = new Map();

  filterKeys.forEach((key) => {
    filterValues[key] = {};
    categories[key] = (metadata[key] || []).map((filter) => {
      if (key === 'priceFilter') {
        const { label, min, max } = filter;
        const priceKey = formatPriceRange(min, max);
        filterValues[key][priceKey] = false;
        displayNameByKey.set(priceKey, label);
        return {
          key: priceKey,
          name: label,
          active: true,
        };
      }

      const { taxonomy, label } = filter;
      // if no taxonomy or label we don't show because we can't filter
      if (taxonomy || label) {
        filterValues[key][taxonomy] = false;
        displayNameByKey.set(taxonomy, label);
        return {
          key: taxonomy,
          name: label,
          active: true,
        };
      }
      return {};
    });
  });

  return {
    categories,
    displayNameByKey,
    filterValues,
  };
};

export const getProductRequest = (
  {
    price,
    filters = {},
    page = 1,
    size = WAFFLE_REQUEST_SIZE,
    sort = 'datePublished:desc',
  },
  giftFinderType,
) => ({
  query: constructQueryObj(filters, giftFinderType),
  price,
  page,
  size,
  sort,
});

export default {
  // constants
  GIFT_FINDER_LABEL,
  DEFAULT_PRICE,
  WAFFLE_REQUEST_SIZE,
  filterKeys,
  giftFinderSortOverride,
  giftFinderTaxonomyOverride,

  // functions
  aggregatePriceRangeFromFilters,
  alterFilterActiveProperty,
  enableSelfAndNext,
  enableSelfAndPrevious,
  extractFromMetadata,
  extractPriceValues,
  formatPriceRange,
  getProductRequest,
  updateEnabledPriceFilters,
};
